import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SUGGESTED_ROUNDS_NAMES } from '../pojo/fund-strategy-constants';
import { GroupFS, RoundsFS, SubGroupFS } from '../pojo/fund-strategy-export-classes';
import {
  FsGroupFormGroup,
  SubGroupFormGroup,
  RoundFormGroup,
} from '../pojo/fund-strategy-export-types';
import { FundStrategyService } from "./fund-strategy-service";

@Injectable({
  providedIn: 'root',
})
export class FundStrategyFormService {
  private fb = inject(FormBuilder);
  private fundStrategyService = inject(FundStrategyService);


  rounds = new FSDataForRounds();

  fillFormFromFs(fs: GroupFS[]): FormGroup {
    if (!fs) {
      return;
    }
    return this.fb.group({
      fundStrategy: this.fb.array(fs.map((group) => this.fillGroupFs(group))),
    });
  }

  fillGroupFs(group: GroupFS): FsGroupFormGroup {
    return this.fb.group({
      name: new FormControl(group.name),
      uuid: new FormControl(group.uuid),
      grOrder: new FormControl(group.grOrder),
      subGroups: this.fb.array(group?.subGroups.map((subGroup) => this.fillSubGroupFs(subGroup))),
    });
  }

  fillSubGroupFs(subGroup: SubGroupFS): SubGroupFormGroup {
    const roundsDataFormArray = subGroup.roundsData.map((roundData) => this.fillRoundsFs(roundData));
    this.rounds.previous.holding = null;
    this.rounds.previous.compNumber = null;

    const subGroupForm = this.fb.group({
      uuid: new FormControl(subGroup.uuid),
      subGrOrder: new FormControl(subGroup.subGrOrder),
      name: new FormControl(subGroup.name),
      roundsData: this.fb.array(roundsDataFormArray),
      total: this.fb.group({
        totalCompaniesNumber: new FormControl(0),
        investmentAmount: new FormControl(0),
        writeOffCompaniesNumber: new FormControl(0),
        exitCompaniesNumber: new FormControl(0),
        round: new FormControl(0),
        investment: new FormControl(0),
        proceeds: new FormControl(0),
        gain: new FormControl(0),
        moic: new FormControl(0),
      }),
    });
     this.fundStrategyService.calculateSubGroupTotals(subGroupForm);
    return subGroupForm;
  }

  fillRoundsFs(roundData: RoundsFS): RoundFormGroup {
    const holding = this.calculateHolding(roundData);
    const calculatedCompaniesNumber = this.calculateCompaniesNumber(roundData);
    return this.fb.group({
      uuid: new FormControl(roundData.uuid),
      roundOrder: new FormControl(roundData.roundOrder),
      companiesNumber: new FormControl(calculatedCompaniesNumber),
      name: new FormControl(roundData.name, { validators: [Validators.required] }),
      esop: new FormControl(roundData.esop),
      preMoney: new FormControl(roundData.preMoney),
      round: new FormControl(roundData.round),
      investment: new FormControl(roundData.investment),
      holding: new FormControl(holding),
      exitCompPrice: new FormControl(roundData.exitCompPrice),
      writeOff: new FormControl(roundData.writeOff),
      exitCompNumber: new FormControl(roundData.exitCompNumber),
      postMoney: new FormControl((roundData.preMoney || 0) + (roundData.round || 0)),
      totalInvestment: new FormControl((calculatedCompaniesNumber || 0) * (roundData.investment || 0)),
      proceeds: new FormControl((holding || 0) * (roundData.exitCompPrice || 0)),
      exitProceeds: new FormControl((holding || 0) * (roundData.exitCompNumber || 0) * (roundData.exitCompPrice || 0)),
      exitMoic: new FormControl(
        (!calculatedCompaniesNumber || !roundData.investment)
          ? 0
          : ((holding || 0) * (roundData.exitCompNumber || 0) * (roundData.exitCompPrice || 0)) /
          ((calculatedCompaniesNumber || 0) * (roundData.investment || 0)),
      ),
    });
  }


  /**
   * Additional holding calculations is necessary, because holding in round 2 depends on the holding in round 1.
   * For this reason we need `this.model.rounds.previous.holding`
   * For the first round and all subsequent rounds we have different formulas
   */
  calculateHolding(roundData: RoundsFS): number {
    const investment = Number(roundData.investment);
    const preMoney = Number(roundData.preMoney);
    const round = Number(roundData.round);
    const esopDicimal = Number(roundData.esop / 100);
    const postMoney = preMoney + round;
    const previousHolding = this.rounds.previous.holding;

    if (previousHolding === null && investment === 0) {
      return;
    }

    if (previousHolding === null) {
      this.rounds.previous.holding = investment / postMoney;
      return this.rounds.previous.holding;
    }

    this.rounds.previous.holding = (investment / postMoney + (previousHolding *
      (preMoney - (postMoney * esopDicimal))) / (preMoney + round));
    return this.rounds.previous.holding;

  }


  /**
   * Calculation of companies number, which depends on writeOff and ExitCompNumber values
   */
  calculateCompaniesNumber(roundData: RoundsFS): number {
    if (this.rounds.previous.compNumber === null && roundData.companiesNumber === 0) {
      return 0;
    };

    if (this.rounds.previous.compNumber === null) {
      const calculatedValue = roundData.companiesNumber - roundData.writeOff - roundData.exitCompNumber;
      this.rounds.previous.compNumber = calculatedValue > 0 ? calculatedValue : 0;
      return roundData.companiesNumber;
    };
    if (this.rounds.previous.compNumber && (roundData.writeOff || roundData.exitCompNumber)) {
      const currentCompNumber = this.rounds.previous.compNumber;
      this.rounds.previous.compNumber = this.rounds.previous.compNumber - roundData.writeOff - roundData.exitCompNumber;
      if (this.rounds.previous.compNumber < 0) {
        this.rounds.previous.compNumber = 0;
      }
      return currentCompNumber;
    }
    if (this.rounds.previous.compNumber && !roundData.writeOff && !roundData.exitCompNumber) {
      return this.rounds.previous.compNumber;
    }
  }


  /**
   * Check companies number, writeOff and ExitCompNumber values, in each round,
   * because they should be less than companies number.
   * Set error and disable states for inputs.
   *
   * @param grIndex index of group
   * @param sbIndex index of sub group
   * @param subGroup FormGroup
   */
  checkWriteOffAndExitValue(subGroup: SubGroupFormGroup, grIndex: number, sbIndex: number): any {

    subGroup.controls.roundsData.controls.forEach((round, rIndex) => {
      const writeOff = Number(round.controls.writeOff.value);
      const companiesNumber = Number(round.controls.companiesNumber.value);
      const exitCompNumber = Number(round.controls.exitCompNumber.value);
      const sumOfWriteOffAndExit = writeOff + exitCompNumber;
      if ((sumOfWriteOffAndExit > companiesNumber) || (sumOfWriteOffAndExit > 0 && Number.isNaN(companiesNumber))) {
        for (let r = 0; r <= rIndex; r++) {
          const key = `${grIndex}-${sbIndex}-${r}`;
          this.rounds.input.error.set(key, true);
        }
      } else {
        const key = `${grIndex}-${sbIndex}-${rIndex}`;
        this.rounds.input.error.set(key, false);
      }
    });
    return this.rounds.input;
  }


  /**
   * Updates the style for unallocated companies within a subgroup (last unchanged companyNumber values).
   * It marks rounds with the last repeated value of companies as unallocated (true),
   * and all others as allocated (false).
   *
   * @param subGroup - The subgroup form group containing rounds data.
   * @param grIndex - The index of the group to which the subgroup belongs.
   * @param sbIndex - The index of the subgroup being processed.
   * @returns An object containing the unallocated companies map and allocation status.
   */
  changeStyleForUnallocatedCompanies(subGroup: SubGroupFormGroup, grIndex: number, sbIndex: number): { unallocatedCompanies: Map<string, boolean>, isAllCompaniesAllocated: boolean } {
    const unallocatedCompanies = new Map<string, boolean>();
    const lastRound = subGroup.getRawValue().roundsData.at(subGroup.getRawValue().roundsData.length - 1);
    const writeOff = Number(lastRound.writeOff);
    const exitCompNumber = Number(lastRound.exitCompNumber);
    const lastCompaniesNumber = Number(lastRound.companiesNumber);

    subGroup.controls.roundsData.controls.forEach((round, rIndex) => {
      const companiesNumber = Number(round.controls.companiesNumber.value);
      const key = `${grIndex}-${sbIndex}-${rIndex}`;
      if (lastCompaniesNumber === 0 || lastCompaniesNumber === (writeOff + exitCompNumber)) {
        unallocatedCompanies.set(key, false);
        // Set true for rounds where companiesNumber matches the last value in the array
      } else {
        unallocatedCompanies.set(key, companiesNumber === lastCompaniesNumber);
      }
    });

    const isAllCompaniesAllocated = !Array.from(unallocatedCompanies.values()).some((isUnAllocated) => isUnAllocated === true);

    return { unallocatedCompanies, isAllCompaniesAllocated };
  }
}


export class FSDataForRounds {
  show = new Map<string, boolean>();
  areInEditMode = false; // needed to show, that rounds edit process is started
  editMode = new Map<string, boolean>();
  suggestedNames = SUGGESTED_ROUNDS_NAMES;
  previous = {
    holding: null as number, // needed for holding calculations. E.x. Holding in round 2 is depends on holding in round 1
    compNumber: null as number, // needed for companies (number) calculations.
                                // E.x. You have 10 companies in round 1. ExitCompanies = 2, WriteOff companies = 3
                                // In the round 2, only 5 companies should be displayed
  };

  input = {
    error: new Map<string, boolean>(),
    disable: new Map<string, boolean>(),
  };
}
